import { Errors, Methods, ResponseCode } from 'enums';

import { ApiResult } from './apiResult';
import { convertResponse } from 'utils';

type SearchParams = {
  [key: string]: unknown;
};

interface Params {
  resource: string;
  options: {
    headers?: HeadersInit;
    searchParams?: SearchParams;
  };
}

interface GetParams extends Params {}
interface PostPrams<R> extends Params {
  options: Params['options'] & {
    body: R;
  };
}

interface GetRequestParams extends GetParams {
  options: GetParams['options'] & {
    method: Methods.GET;
  };
}

interface PostRequestParams<T> extends PostPrams<T> {
  options: PostPrams<T>['options'] & {
    method: Methods.POST;
  };
}

class Api {
  defaultHeaders: HeadersInit;

  constructor() {
    this.defaultHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  }

  get<T, U>({ resource, options }: GetParams) {
    return this.makeRequest<unknown, T, U>({
      resource,
      options: {
        method: Methods.GET,
        ...options,
      },
    });
  }

  post<R, T, U>({ resource, options }: PostPrams<R>) {
    return this.makeRequest<R, T, U>({
      resource,
      options: {
        method: Methods.POST,
        ...options,
      },
    });
  }

  async makeRequest<R, T, U>({ resource, options }: GetRequestParams | PostRequestParams<R>) {
    const url = this.prepareUrl(resource, options.searchParams);

    const requestOptions: RequestInit = {
      method: options.method,
      mode: 'cors',
      headers: {
        ...this.defaultHeaders,
        ...options?.headers,
      },
      ...(options.method === Methods.POST && {
        body: JSON.stringify(options.method === Methods.POST && options.body),
      }),
    };

    const result = await fetch(url, requestOptions).then(
      (response: Response) => {
        switch (response.status) {
          case 200:
            return response.json();
          case 400:
            throw new Error(Errors.VALIDATION);
          case 401:
            throw new Error(Errors.UNAUTHORIZED);
          default:
            throw new Error(Errors.ERROR);
        }
      },
      () => {
        throw new Error(Errors.CONNECTION_ERROR);
      }
    );

    if (result.code && result.code !== ResponseCode.OK) {
      throw new Error(Errors.ERROR);
    }
    return new ApiResult<T, U>(convertResponse<T, U>(result));
  }

  prepareUrl(resource: string, searchParams: SearchParams = {}) {
    const url = Object.entries(searchParams).reduce((acc: string, val: Array<unknown>, index) => {
      if (index === 0) {
        acc += `?${val[0]}=${val[1]}`;
        return acc;
      }
      acc += `&${val[0]}=${val[1]}`;
      return acc;
    }, `${resource}`);

    return url;
  }
}
const API = new Api();

export default API;
